import type { VerifyDomainResult } from '@/features/domains/types';

export type DnsVerificationSummary = {
  tone: 'success' | 'error' | 'warning';
  message: string;
};

const plural = (count: number, singular: string) => `${count} ${singular}${count === 1 ? '' : 's'}`;

const isOwnershipRecord = (record: NonNullable<VerifyDomainResult['records']>[number]) => {
  if (record.purpose?.toLowerCase() === 'verification') return true;
  const firstLabel = record.host?.toLowerCase().replace(/\.$/, '').split('.')[0];
  return firstLabel === '_nifty-verify' || firstLabel === 'nifty-verify';
};

/**
 * Summarise the completed, server-side DNS check. Ownership is deliberately
 * excluded after initial verification: that one-time token may be removed,
 * while every displayed mail-authentication and routing record must pass.
 */
export function summarizeDnsVerification(result: VerifyDomainResult): DnsVerificationSummary {
  if (!result.verified) {
    return {
      tone: 'error',
      message: 'Ownership record not found yet — DNS may still be propagating',
    };
  }

  const records = (result.records ?? []).filter((record) => !isOwnershipRecord(record));
  if (records.length === 0) {
    return {
      tone: 'warning',
      message: 'DNS check completed, but no mail records were returned. Please try again.',
    };
  }

  const ok = records.filter((record) => record.status === 'ok').length;
  const missing = records.filter((record) => record.status === 'missing').length;
  const conflicting = records.filter((record) => record.status === 'mismatch').length;
  const pending = records.filter((record) => record.status === 'pending').length;

  if (ok === records.length) {
    const verb = records.length === 1 ? 'is' : 'are';
    return { tone: 'success', message: `All ${plural(records.length, 'DNS record')} ${verb} correct` };
  }

  const issues = [
    missing > 0 ? plural(missing, 'missing') : '',
    conflicting > 0 ? plural(conflicting, 'conflict') : '',
    pending > 0 ? plural(pending, 'still pending') : '',
  ].filter(Boolean).join(', ');

  return {
    tone: 'error',
    message: `DNS check complete: ${ok} of ${records.length} records correct — ${issues}`,
  };
}
